import {
  CreateSmsFragment,
  ListSmsFragments,
  ResponseError,
  ShowSmsFragment,
  SitesApi,
  SmsFragmentsApi,
  UpdateSmsFragment,
  UpdateSmsNotificationSettings,
} from '@trybeapp/sdk'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { usePageQueryParam } from 'hooks/UsePageQueryParam'
import { UseQueryOptions, UseQueryResult, useMutation, useQuery, useQueryClient } from 'react-query'
import { apiConfiguration } from 'utilities/TrybeApiClient'

const api = new SitesApi(apiConfiguration)
const fragmentsApi = new SmsFragmentsApi(apiConfiguration)

export const useSmsSettings = (options: UseQueryOptions = {}): UseQueryResult<any> => {
  const siteId = useCurrentSiteId()
  return useQuery(
    ['smsSettings', siteId],
    () => api.getSmsNotificationSettings({ siteId }),
    options
  )
}

export const useUpdateSmsSettings = () => {
  const siteId = useCurrentSiteId()
  const queryClient = useQueryClient()

  return useMutation(
    (updateSmsNotificationSettings: UpdateSmsNotificationSettings) =>
      api.updateSmsNotificationSettings({ siteId, updateSmsNotificationSettings }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['smsSettings', siteId])
      },
    }
  )
}

export const useSmsFragments = (params: any = {}, options = {}) => {
  params.siteId = useCurrentSiteId()
  const [page] = usePageQueryParam()

  return useQuery<ListSmsFragments, ResponseError>(
    ['smsFragments', { params }],
    () => fragmentsApi.listSmsFragments({ ...params, page }),
    options
  )
}

export const useSmsFragment = (smsFragmentId: string, options = {}) => {
  const siteId = useCurrentSiteId()

  return useQuery<ShowSmsFragment>(
    ['smsFragments', { siteId, smsFragmentId }],
    async () =>
      await fragmentsApi.getSmsFragment({
        smsFragmentId,
      }),
    options
  )
}

export const useCreateSmsFragment = () => {
  const siteId = useCurrentSiteId()
  const queryClient = useQueryClient()

  return useMutation(
    async (smsFragment: CreateSmsFragment) =>
      await fragmentsApi.createSmsFragment({
        createSmsFragment: {
          ...smsFragment,
          site_id: siteId,
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('smsFragments')
      },
    }
  )
}

export const useUpdateSmsFragment = () => {
  const queryClient = useQueryClient()

  return useMutation<ShowSmsFragment, ResponseError, [string, UpdateSmsFragment]>(
    async ([smsFragmentId, updateSmsFragment]) =>
      await fragmentsApi.updateSmsFragment({
        smsFragmentId,
        updateSmsFragment,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('smsFragments')
      },
    }
  )
}

export const useArchiveSmsFragment = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async (smsFragmentId: string) =>
      await fragmentsApi.deleteSmsFragment({
        smsFragmentId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('smsFragments')
      },
    }
  )
}

export const useRestoreSmsFragment = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (smsFragmentId: string) => fragmentsApi.restoreSmsFragment({ smsFragmentId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('smsFragments')
      },
    }
  )
}
